/**
 * Central cache-invalidation bus.
 *
 * Read-side caches (stek list, worktree, entity detail, …) go stale when a
 * mutation lands but nothing tells them to refresh. Historically each store
 * subscribed to the sync store with its own hard-coded *attribute* whitelist
 * (e.g. only "name"/"description"), so any field outside that list — logos,
 * relationships, … — was silently missed.
 *
 * This bus inverts that: a cache registers once, keyed by the *entity* it owns
 * (via a predicate over the change), and is refreshed whenever ANY change to
 * that entity lands. Forgetting a field can now only cost a harmless extra
 * refresh, never staleness. A single sync producing several changes refreshes
 * each cache at most once (deduped per batch).
 *
 * Producers: the sync store (this tab's own saves, see its `$onAction("sync")`
 * wiring) and the threads store, which announces what the thread agent changed
 * on the branch as its action entries land over SSE.
 */

/** Structurally compatible with the sync store's `SyncIdentity`. */
export type EntityChange = {
    organizationId?: string;
    entityType: string;
    entityId: string;
    attribute?: string;
    /**
     * How the change reached this tab. A local sync already has the newest
     * entity-detail state in memory; detail editors must not refetch it and
     * replace their live editor state. Other read-side caches (worktree,
     * lists, thread changes, ...) still consume the same invalidation.
     *
     * `thread-agent` is an EXTERNAL change (the agent wrote it on the branch,
     * this tab has none of it in memory), so detail editors DO refetch — under
     * their usual clobber guard, which is what protects a half-typed edit.
     */
    origin?: "local-sync" | "thread-agent";
};

type Predicate = (change: EntityChange) => boolean;
/**
 * Receives the changes from the batch that satisfied the predicate. Most caches
 * refetch wholesale and ignore the argument; a cache that keeps per-entity
 * derived state (e.g. the business-entity attribute cache behind the class
 * diagram) needs the ids to evict precisely.
 */
type Handler = (matched: readonly EntityChange[]) => void;

type Registration = { match: Predicate; run: Handler };

const registrations = new Set<Registration>();

/**
 * Register a cache invalidator. `run` is invoked when a notified change
 * satisfies `match`. Returns a disposer that removes the registration.
 */
export function onEntityChange(match: Predicate, run: Handler): () => void {
    const registration = { match, run };
    registrations.add(registration);
    return () => {
        registrations.delete(registration);
    };
}

/**
 * Notify the bus that a batch of entity changes has landed. Every registration
 * whose predicate matches at least one change runs exactly once, receiving all
 * of the batch's changes that it matched.
 */
export function notifyEntityChanged(changes: readonly EntityChange[]): void {
    // Keyed by the registration, not by its handler: two registrations may
    // share one function reference, and merging them would hand each the other's
    // matches.
    const toRun = new Map<Registration, EntityChange[]>();
    for (const change of changes) {
        for (const registration of registrations) {
            if (registration.match(change)) {
                const matched = toRun.get(registration);
                if (matched) {
                    matched.push(change);
                } else {
                    toRun.set(registration, [change]);
                }
            }
        }
    }
    toRun.forEach((matched, registration) => registration.run(matched));
}

/** Test-only: drop all registrations so specs start from a clean bus. */
export function __resetCacheInvalidation(): void {
    registrations.clear();
}
